
import { Vue, Component } from 'vue-property-decorator'
import axios from 'axios'
import { notification } from 'ant-design-vue'
import PageHeader from '@/components/base/PageHeader.vue'
import { KAMForm } from '@/types/kam'
import { KAMFormStatuses, KAMFormStatusLabelColors } from '@/helpers/kamFormStates'
import { getKamFormById, getKamFormByReference, getKamFormByReferenceLatest } from '@/views/kam/formRegistry'
import KAMGenericForm from '@/views/kam/forms/KAMGenericForm.vue'

@Component({
  components: { PageHeader, KAMGenericForm }
})
export default class KAMFormEdit extends Vue {
  KAMFormStatuses = KAMFormStatuses
  loadFailed = false
  selectedComponent: any = null
  previewingPdf = false

  mounted () : void {
    this.load()
  }

  beforeRouteLeave (to: any, from: any, next: (value?: boolean) => void) : void {
    this.$store.dispatch('clearCurrentKAMForm')
    next()
  }

  async load () : Promise<void> {
    this.loadFailed = false
    try {
      const item = await this.$store.dispatch('loadKAMFormById', this.$route.params.kamFormId)
      this.resolveComponent(item)
    } catch {
      this.loadFailed = true
    }
  }

  resolveComponent (item: KAMForm) : void {
    const form = item.form
    const version = form?.version != null ? Number(form.version) : null
    const entry = getKamFormByReference(form?.reference, version) ||
      getKamFormByReferenceLatest(form?.reference) ||
      getKamFormByReferenceLatest(this.$route.params.formReference ? String(this.$route.params.formReference) : null) ||
      getKamFormById(form?.id)
    this.selectedComponent = entry?.component || null
  }

  async downloadKAMForm () : Promise<void> {
    if (!this.currentItem) return
    try {
      const response = await axios.get(process.env.VUE_APP_API_URL + '/kamForms/' + this.currentItem.id + '/export', { responseType: 'arraybuffer' })
      const blob = new Blob([response.data], { type: 'application/pdf' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `KAM_${this.filenamePart(this.currentItem.form?.name || 'formulier')}_${this.currentItem.project?.reference || 'project'}_${this.currentItem.id}.pdf`
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)
    } catch {
      notification.error({ message: 'Download mislukt.', description: 'Het downloaden van het KAM-formulier is mislukt.', duration: 0 })
    }
  }

  async previewKAMForm () : Promise<void> {
    if (!this.currentItem) return
    this.previewingPdf = true
    try {
      const response = await axios.get(process.env.VUE_APP_API_URL + '/kamForms/' + this.currentItem.id + '/export', { responseType: 'arraybuffer' })
      const blob = new Blob([response.data], { type: 'application/pdf' })
      const url = URL.createObjectURL(blob)
      window.open(url, '_blank', 'noopener')
      setTimeout(() => URL.revokeObjectURL(url), 60000)
    } catch {
      notification.error({ message: 'Preview mislukt.', description: 'De PDF-preview kon niet worden geopend.', duration: 0 })
    } finally {
      this.previewingPdf = false
    }
  }

  people (key: string) : any[] {
    return (this.currentItem?.filters?.[key] || []).map((person: any) => {
      const label = person.label || person.full_name || person.name || ''
      return {
        ...person,
        label,
        value: person.id,
        full_name: label,
        name: label
      }
    })
  }

  statusColor (status: number) : string {
    return KAMFormStatusLabelColors[status] || 'default'
  }

  filenamePart (value: string) : string {
    return value
      .replace(/[^a-z0-9_-]+/gi, '_')
      .replace(/^_+|_+$/g, '')
      .toLowerCase() || 'formulier'
  }

  get currentItem () : KAMForm|null {
    return this.$store.getters.currentKAMForm
  }

  get safeAnswers () : Record<string, any> {
    const answers = this.currentItem?.answers
    return answers && typeof answers === 'object' && !Array.isArray(answers) ? answers : {}
  }

  get safeMedia () : Record<string, any> {
    const media = this.safeAnswers.media
    return media && typeof media === 'object' && !Array.isArray(media) ? media : {}
  }

  get resolvedComponent () : any {
    return this.selectedComponent || KAMGenericForm
  }

  get componentKey () : string {
    const form = this.currentItem?.form
    return [
      this.currentItem?.id || 'new',
      form?.reference || 'unknown',
      form?.version || 'latest'
    ].join(':')
  }

  get loading () : boolean {
    return this.$store.getters.loadingKAMForm.item
  }

  get headerTitle () : string {
    if (!this.currentItem) return 'KAM formulier'
    const form = this.currentItem.form
    return `${form?.name || 'Formulier'} #${this.currentItem.id}`
  }

  get projectLabel () : string {
    const project = this.currentItem?.project
    return project ? `${project.name} (${project.reference})` : '-'
  }

  get formLabel () : string {
    const form = this.currentItem?.form
    if (!form) return '-'
    return form.name
  }

  formatDate (value?: string|null) : string {
    return value ? new Date(value).toLocaleString('nl-NL') : '-'
  }

  get projectLocation () : string {
    const project = this.currentItem?.project as { city?: string } | undefined
    return project?.city || this.safeAnswers.projectLocation || ''
  }

  get hasExternalClientInfo () : boolean {
    return typeof this.safeAnswers.externalClient !== 'undefined'
  }

  get externalClientLabel () : string {
    return this.safeAnswers.externalClient ? 'Ja' : 'Nee'
  }

  get gridColSpan () : number {
    const extraCount = (this.projectLocation ? 1 : 0) + (this.hasExternalClientInfo ? 1 : 0)
    return extraCount > 0 ? 4 : 6
  }

  get saving () : boolean {
    return this.$store.getters.loadingKAMForm?.save || false
  }

  async saveKAMForm () : Promise<void> {
    if (!this.currentItem) return
    try {
      await this.$store.dispatch('saveKAMForm', {
        id: this.currentItem.id,
        answers: this.safeAnswers
      })
      notification.success({ message: 'Opgeslagen', description: 'Het KAM-formulier is succesvol opgeslagen.' })
    } catch {
      // Notification already shown by store
    }
  }
}
